import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Header } from '@/components/Header';
import BottomNav from '@/components/BottomNav';
import SiteFooter from '@/components/SiteFooter';
import ContributorRequestForm from '@/components/ContributorRequestForm';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { apiFetch } from '@/hooks/useFetch';
import {
  Search, Calendar, Eye, ArrowLeft, ArrowRight, Tag, PenLine, Heart,
  ArrowUpAZ, ArrowDownAZ, ArrowUp10, ArrowDown10,
} from 'lucide-react';
import { Seo } from '@/components/Seo';
import { MostLovedCard } from '@/components/MostLovedCard';
import BlogSubscribeWidget from '@/components/blog/BlogSubscribeWidget';
import AdSenseUnit from '@/components/AdSenseUnit';

const BlogPage = () => {
  const { t, isSpanish } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canWrite = user?.is_admin || user?.is_contributor;

  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [favorites, setFavorites] = useState(new Set());

  // Load the user's favorited post ids whenever the user changes
  useEffect(() => {
    if (!user) { setFavorites(new Set()); return; }
    (async () => {
      const { data } = await apiFetch('/api/blog/posts/favorites');
      if (Array.isArray(data)) {
        setFavorites(new Set(data.map((p) => p.post_id)));
      }
    })();
  }, [user]);

  const toggleFavorite = async (postId, e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!user) {
      navigate('/login');
      return;
    }
    // Optimistic update
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) next.delete(postId); else next.add(postId);
      return next;
    });
    const { data, error } = await apiFetch(`/api/blog/posts/${postId}/favorite`, { method: 'POST' });
    if (error) {
      // Rollback
      setFavorites((prev) => {
        const next = new Set(prev);
        if (next.has(postId)) next.delete(postId); else next.add(postId);
        return next;
      });
    } else if (data && typeof data.favorited === 'boolean') {
      setFavorites((prev) => {
        const next = new Set(prev);
        if (data.favorited) next.add(postId); else next.delete(postId);
        return next;
      });
    }
  };

  const currentPage = parseInt(searchParams.get('page') || '1');
  const activeCategory = searchParams.get('category') || '';
  const activeTag = searchParams.get('tag') || '';
  const sortMode = searchParams.get('sort') || 'date_desc';

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    let url = `/api/blog/posts?page=${currentPage}&limit=24`;
    if (activeCategory) url += `&category=${encodeURIComponent(activeCategory)}`;
    if (activeTag) url += `&tag=${encodeURIComponent(activeTag)}`;
    if (isSpanish) url += `&lang=es`;
    const { data } = await apiFetch(url);
    if (data) {
      setPosts(data.posts || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    }
    setLoading(false);
  }, [currentPage, activeCategory, activeTag, isSpanish]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  // Client-side sort over the current page's posts. Backend already returns
  // newest-first; this lets the user re-sort A→Z / Z→A / most-viewed.
  const sortedPosts = React.useMemo(() => {
    const list = [...posts];
    list.sort((a, b) => {
      const aT = (a.title || '').toLowerCase();
      const bT = (b.title || '').toLowerCase();
      const aD = a.published_at || a.created_at || '';
      const bD = b.published_at || b.created_at || '';
      const aV = a.views || 0;
      const bV = b.views || 0;
      switch (sortMode) {
        case 'date_asc':  return aD.localeCompare(bD);
        case 'title_asc': return aT.localeCompare(bT);
        case 'title_desc': return bT.localeCompare(aT);
        case 'views_desc': return bV - aV;
        case 'views_asc':  return aV - bV;
        case 'date_desc':
        default:           return bD.localeCompare(aD);
      }
    });
    return list;
  }, [posts, sortMode]);

  useEffect(() => {
    (async () => {
      const [catsRes, tagsRes] = await Promise.all([
        apiFetch('/api/blog/categories'),
        apiFetch('/api/blog/tags')
      ]);
      if (catsRes.data) {
        // New API returns { categories: [{slug,label,published_count,...}] };
        // old shape was [{name, count}]. Normalise so the rest of the page
        // can keep using `c.name` semantics. Hide buckets with zero
        // published posts so we never advertise dead links.
        const list = Array.isArray(catsRes.data) ? catsRes.data : (catsRes.data.categories || []);
        setCategories(
          list
            .map((c) => ({
              name: c.slug || c.name,
              label: c.label || c.name,
              count: c.published_count ?? c.count ?? 0,
            }))
            .filter((c) => (c.count ?? 0) > 0)
        );
      }
      if (tagsRes.data) setTags(tagsRes.data);
    })();
  }, []);

  // slug → human label lookup so badges/legacy posts always show the
  // friendly category name instead of either the slug ("how-to-guides")
  // or whatever raw string the post happens to have stored from the
  // pre-migration era.
  const categoryLabel = useMemo(() => {
    const m = {};
    for (const c of categories) {
      if (c.name) m[c.name] = c.label || c.name;
      if (c.label) m[c.label.toLowerCase()] = c.label;
    }
    return (raw) => {
      if (!raw) return raw;
      return m[raw] || m[String(raw).toLowerCase()] || raw;
    };
  }, [categories]);

  // Multi-key setFilter: caller passes an object so we don't double-write
  // searchParams in the same tick (which was a stale-snapshot bug that
  // made the "All" badge effectively a no-op when both category+tag were
  // active). Single-key calls still work via setFilter(key, value).
  const setFilter = (keyOrUpdates, maybeValue) => {
    const params = new URLSearchParams(searchParams);
    const updates =
      typeof keyOrUpdates === 'string'
        ? { [keyOrUpdates]: maybeValue }
        : keyOrUpdates;
    let touchedNonPage = false;
    Object.entries(updates).forEach(([k, v]) => {
      if (v) params.set(k, String(v));
      else params.delete(k);
      if (k !== 'page') touchedNonPage = true;
    });
    if (touchedNonPage) params.set('page', '1');
    setSearchParams(params);
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <Seo
        path="/blog"
        title="Blog — ClearCommunicationApp"
        description="Articles on neurodivergent communication, ADHD, autism support, language learning, and bridging communication gaps in everyday life."
      />
      <Header />
      <div className="container py-3 sm:py-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-1">
            <h1 className="text-2xl sm:text-3xl font-bold" data-testid="blog-title">
              {t('blog')}
            </h1>
            {canWrite && (
              <Button
                onClick={() => navigate('/blog/admin')}
                className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d]"
                size="sm"
                data-testid="write-post-btn"
              >
                <PenLine className="h-4 w-4 mr-1" />
                {t('writeAPost')}
              </Button>
            )}
          </div>
          <p className="text-muted-foreground text-sm mb-1">
            {t('blogSubtitle')}
          </p>
          <Link
            to="/contributors"
            className="text-sm text-[#00E5CC] hover:underline inline-flex items-center gap-1 mb-3"
            data-testid="contributors-link"
          >
            Meet our contributors →
          </Link>

          {/* Category & Tag filters */}
          {(categories.length > 0 || tags.length > 0) && (
            <div className="flex flex-wrap gap-2 mb-3" data-testid="blog-category-filters">
              <Badge
                variant={!activeCategory && !activeTag ? 'default' : 'secondary'}
                className="cursor-pointer"
                onClick={() => setFilter({ category: '', tag: '' })}
                data-testid="blog-cat-all"
              >
                {t('all')}
              </Badge>
              {categories.map(c => (
                <Badge
                  key={c.name}
                  variant={activeCategory === c.name ? 'default' : 'secondary'}
                  className="cursor-pointer"
                  onClick={() => setFilter('category', activeCategory === c.name ? '' : c.name)}
                  data-testid={`blog-cat-${c.name}`}
                >
                  {c.label || c.name}{c.count != null ? ` (${c.count})` : ''}
                </Badge>
              ))}
            </div>
          )}

          {/* Sort selector — client-side reorder of the current page's
              posts. Useful for browsing alphabetically or by popularity
              without losing the active category/tag filter. */}
          <div className="flex items-center justify-end gap-2 mb-3">
            <span className="text-xs text-muted-foreground">{isSpanish ? 'Ordenar:' : 'Sort:'}</span>
            <Select
              value={sortMode}
              onValueChange={(v) => setFilter('sort', v === 'date_desc' ? '' : v)}
            >
              <SelectTrigger
                className="w-[180px] h-8 text-xs"
                data-testid="blog-sort-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date_desc">{isSpanish ? 'Más nuevos primero' : 'Newest first'}</SelectItem>
                <SelectItem value="date_asc">{isSpanish ? 'Más antiguos primero' : 'Oldest first'}</SelectItem>
                <SelectItem value="title_asc">{isSpanish ? 'Título A → Z' : 'Title A → Z'}</SelectItem>
                <SelectItem value="title_desc">{isSpanish ? 'Título Z → A' : 'Title Z → A'}</SelectItem>
                <SelectItem value="views_desc">{isSpanish ? 'Más vistos' : 'Most viewed'}</SelectItem>
                <SelectItem value="views_asc">{isSpanish ? 'Menos vistos' : 'Least viewed'}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Most-loved snapshot — only renders when there are recent
              reactions; auto-hides on a brand-new site. */}
          <div className="mb-3">
            <MostLovedCard isSpanish={isSpanish} days={7} limit={5} />
          </div>

          {/* Email subscribe widget — readers pick the categories they
              want notified about. Double opt-in flow. */}
          <div className="mb-3">
            <BlogSubscribeWidget categories={categories} isSpanish={isSpanish} />
          </div>

          {/* Posts grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="flex gap-1">
                <div className="w-2 h-2 rounded-full bg-[#00E5CC] animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-[#00E5CC] animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full bg-[#00E5CC] animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-lg">
                {t('noBlogPosts')}
              </p>
            </div>
          ) : (
            <>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {sortedPosts.map(post => (
                <Card
                  key={post.post_id}
                  className="cursor-pointer hover:shadow-lg transition-shadow group overflow-hidden relative"
                  onClick={() => navigate(`/blog/${post.post_id}`)}
                  data-testid={`blog-post-${post.post_id}`}
                >
                  {/* Favorite ❤ — top-right corner, layered above cover */}
                  <button
                    type="button"
                    onClick={(e) => toggleFavorite(post.post_id, e)}
                    aria-label={favorites.has(post.post_id) ? 'Remove from favorites' : 'Add to favorites'}
                    data-testid={`favorite-blog-${post.post_id}`}
                    className="absolute top-2 right-2 z-10 h-9 w-9 rounded-full bg-card/90 backdrop-blur-sm shadow flex items-center justify-center hover:bg-card transition-colors"
                  >
                    <Heart
                      className={`h-5 w-5 transition-all ${
                        favorites.has(post.post_id)
                          ? 'fill-red-500 text-red-500 scale-110'
                          : 'text-muted-foreground'
                      }`}
                    />
                  </button>
                  {post.cover_image ? (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={post.cover_image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                      />
                    </div>
                  ) : (
                    // Branded fallback so cards stay visually consistent
                    // when an admin hasn't uploaded a cover yet. Uses the
                    // post's category (or title initial) as the visual hook.
                    <div className="aspect-video overflow-hidden bg-gradient-to-br from-[#00E5CC]/30 via-[#7C3AED]/20 to-[#F97316]/20 flex items-center justify-center p-4">
                      <div className="text-center">
                        <div className="text-3xl sm:text-4xl font-extrabold text-[#0d1f2d]/80 dark:text-white/90 tracking-tight line-clamp-2">
                          {post.title?.slice(0, 32) || 'ClearCommunicationApp'}
                        </div>
                        {post.category && (
                          <div className="text-[10px] uppercase tracking-widest text-[#00A896] mt-2 font-semibold">
                            {categoryLabel(post.category)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <CardContent className="p-4">
                    {post.category && (
                      <Badge variant="secondary" className="mb-2 text-xs">{categoryLabel(post.category)}</Badge>
                    )}
                    <h2 className="font-bold text-lg mb-2 line-clamp-2 group-hover:text-[#00E5CC] transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {post.published_at ? new Date(post.published_at).toLocaleDateString() : ''}
                      </div>
                      <div className="flex items-center gap-1">
                        <Eye className="h-3 w-3" />
                        {post.views || 0}
                      </div>
                    </div>
                    {post.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {post.tags.slice(0, 3).map(t => (
                          <span key={t} className="text-[10px] px-1.5 py-0.5 bg-muted rounded-full">{t}</span>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
            {/* In-feed AdSense — placed after the post grid so it renders
                only after publisher content. Only shows for guests/free users. */}
            <AdSenseUnit type="in-feed" className="mt-8" />
            </>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setFilter('page', String(currentPage - 1))}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <span className="flex items-center text-sm px-3">
                {currentPage} / {pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage >= pages}
                onClick={() => setFilter('page', String(currentPage + 1))}
              >
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Contributor invitation — visible to logged-in users who aren't
            already admin/contributor. Hidden once promoted. */}
        {user && !user.is_admin && !user.is_contributor && (
          <div className="container max-w-3xl mx-auto px-4 mb-12">
            <ContributorRequestForm isSpanish={isSpanish} variant="inline" />
          </div>
        )}
      </div>
      <SiteFooter />
      <BottomNav />
    </div>
  );
};

export default BlogPage;
